/** Illustrations — miniature wireframe renderings of Formency interfaces. */
import {
  SessionPlayer,
  CreateLesson,
  ReviseSession,
  SearchDiscover,
  CourseStructure,
  LessonEditor,
  InsightsDashboard,
  StreakCalendar,
  CourseEditor,
  StepCard,
} from '@/shared/illustrations';
import type { IllustrationProps } from '@/shared/illustrations';
import { Section } from '../shared';

// ─── Data ────────────────────────────────────────────────────────────────────

type Entry = {
  name: string;
  description: string;
  Component: React.ComponentType<IllustrationProps>;
  width: number;
};

const ILLUSTRATIONS: Entry[] = [
  {
    name: 'SessionPlayer',
    description: 'MCQ session — progress bar, question, 2×2 grid, check button',
    Component: SessionPlayer,
    width: 120,
  },
  {
    name: 'CreateLesson',
    description: 'CreateBox — textarea, suggestion chips, attach, generate',
    Component: CreateLesson,
    width: 120,
  },
  {
    name: 'ReviseSession',
    description: 'Revision card — accuracy hero, focus areas, browse button',
    Component: ReviseSession,
    width: 120,
  },
  {
    name: 'SearchDiscover',
    description: 'Search bar + 2×2 course card grid with gradient headers',
    Component: SearchDiscover,
    width: 120,
  },
  {
    name: 'CourseStructure',
    description: 'Course — gradient header, module/topic tree, progress bar',
    Component: CourseStructure,
    width: 120,
  },
  {
    name: 'LessonEditor',
    description: 'Step list — drag handles, type badges, add button',
    Component: LessonEditor,
    width: 120,
  },
  {
    name: 'InsightsDashboard',
    description: 'Stats row, weekly bar chart, accuracy ring, streak card',
    Component: InsightsDashboard,
    width: 120,
  },
  {
    name: 'StreakCalendar',
    description: '7-day streak grid with check marks, flame accent',
    Component: StreakCalendar,
    width: 150,
  },
  {
    name: 'CourseEditor',
    description: 'Studio — sidebar tree nav, tabs, lesson cards, publish button',
    Component: CourseEditor,
    width: 160,
  },
  {
    name: 'StepCard',
    description: 'Single step — type badge, rich text prompt, MCQ config',
    Component: StepCard,
    width: 120,
  },
];

// ─── Preview card ────────────────────────────────────────────────────────────

function IllustrationPreview({ entry }: { entry: Entry }) {
  const { name, description, Component, width } = entry;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Name + description */}
      <div>
        <p
          style={{
            fontSize: 15,
            fontWeight: 600,
            color: 'var(--color-text)',
            letterSpacing: '-0.01em',
            marginBottom: 2,
          }}
        >
          {name}
        </p>
        <p style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{description}</p>
      </div>

      {/* Variant row */}
      <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
        {/* On-gradient */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span
            style={{
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: 'var(--color-text-muted)',
            }}
          >
            on-gradient
          </span>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 20,
              borderRadius: 12,
              background:
                'linear-gradient(135deg, #ff9b4a 0%, #e86a9e 40%, #9b6bda 75%, #6b5ce7 100%)',
              minWidth: width + 40,
            }}
          >
            <Component variant="on-gradient" style={{ width }} />
          </div>
        </div>

        {/* On-surface */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span
            style={{
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: 'var(--color-text-muted)',
            }}
          >
            on-surface
          </span>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 20,
              borderRadius: 12,
              background: 'var(--color-surface)',
              border: '1px solid var(--color-border)',
              minWidth: width + 40,
            }}
          >
            <Component variant="on-surface" style={{ width }} />
          </div>
        </div>

        {/* On-surface large */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span
            style={{
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: 'var(--color-text-muted)',
            }}
          >
            on-surface (large)
          </span>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 28,
              borderRadius: 12,
              background: 'var(--color-surface)',
              border: '1px solid var(--color-border)',
              minWidth: width * 1.6 + 56,
            }}
          >
            <Component variant="on-surface" style={{ width: width * 1.6 }} />
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Tab ─────────────────────────────────────────────────────────────────────

export default function IllustrationsTab() {
  return (
    <div style={{ paddingTop: 32 }}>
      <Section label="UI Illustrations">
        <p
          style={{
            fontSize: 14,
            color: 'var(--color-text-secondary)',
            lineHeight: 1.6,
            marginBottom: 32,
            maxWidth: 600,
          }}
        >
          Miniature wireframe renderings of actual Formency interfaces. Each illustration supports
          two rendering contexts via the <code style={{ fontSize: 12 }}>variant</code> prop:{' '}
          <code style={{ fontSize: 12 }}>&quot;on-gradient&quot;</code> for gradient backgrounds and{' '}
          <code style={{ fontSize: 12 }}>&quot;on-surface&quot;</code> for light/dark surfaces.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 48 }}>
          {ILLUSTRATIONS.map((entry) => (
            <IllustrationPreview key={entry.name} entry={entry} />
          ))}
        </div>
      </Section>
    </div>
  );
}
